import React, { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import SubmitButton from './SubmitButton';
import QuestionButton from './QuestionButton';
import Header from './Header';
import InputField from './InputField';

import getEnv from '../utils/getEnv';
import { HTTP_NOT_FOUND, HTTP_BAD_REQUEST, HTTP_UNAUTHORIZED } from '../constants';

/**
 * Functional component that initializes the contact information form
 *
 * @param {object} props - properties given to the component
 */
const ContactForm = (props) => {
    const { t } = useTranslation();
    const reqFieldsMsg = t('fieldRequired');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [smsCode, setSmsCode] = useState('');
    const [blockSms, toggleBlockSms] = useState(false);
    const [codeHidden, toggleCodeHidden] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [errorFields, setErrorFields] = useState('');
    const [errorFieldsMsg, setErrorFieldsMsg] = useState(reqFieldsMsg);
    const [errorStyle, setErrorStyle] = useState({});
    const [codeErrorClass, setCodeErrorClass] = useState('help-block help-block-black text-align-center');
    const [submitStep, setSubmitStep] = useState('sms_needed');

    let apiHost;
    if (getEnv('NODE_ENV') === 'production') {
        apiHost = window._env_.REACT_APP_API_HOST;
    } else {
        apiHost = getEnv('REACT_APP_API_HOST', 'http://localhost:8085');
    }
    const apiUrl = `${apiHost}/contacts/`;
    const smsApiUrl = `${apiHost}/check-keysms/`;

    const { step, setStep, address, headers, setHeaders, setCommonErrorMsg } = props;

    /**
     * Handles the onChange events on input fields. Regex checks if phone input
     * contains only numbers.
     *
     * @param {*} e - the event fired (onChange)
     * @param {Function} changeHandler - function to handle the changes (setState hook)
     */
    const handleChange = (e, changeHandler) => {
        const allowDigits = /(^\d+$)/;
        changeHandler(e.target.value);
        setErrorFields('');
        setErrorStyle({});
        if (e.target.name === 'phone' && !allowDigits.exec(e.target.value)) {
            setErrorFieldsMsg(t('onlyDigits'));
            setErrorFields('phone');
            setErrorStyle({ borderColor: '#ff2222' });
        }
    };

    /**
     * Call the api that sends message with the sms verification code
     *
     * @param {*} e - the onSubmit event
     */
    const getKeySms = (e) => {
        setErrorMsg(t('smsSent'));
        const config = {
            headers: headers,
        };
        const postData = new URLSearchParams();
        postData.append('code', null);
        postData.append('phone', phone);
        axios
            .post(smsApiUrl, postData, config)
            .then((resp) => {
                if (resp.status === 201) {
                    setHeaders({ ...headers, Authorization: resp.data.jwt });
                    setSubmitStep('sms_arrived');
                    toggleCodeHidden(false);
                } else {
                    setErrorMsg(t('enterCode'));
                    return;
                }
            })
            .catch((err) => {
                console.log(`%cError: ${err}`, 'color: #E8178C');
                setErrorMsg(t('errorUnexpected'));
                return;
            });
        e.preventDefault();
    };

    /**
     * Verify the code sent by keysms, then proceed to the actual form submit
     *
     * @param {*} e - the onSubmit event
     */
    const verifyKeySms = (e) => {
        const config = {
            headers: headers,
        };
        const postData = new URLSearchParams();
        postData.append('code', smsCode);
        axios
            .post(smsApiUrl, postData, config)
            .then((resp) => {
                if (resp.status === 200) {
                    setErrorMsg('');
                    setSubmitStep('sms_ok');
                    submitContacts(e);
                } else {
                    setCodeErrorClass('help-block help-block-red text-align-center');
                    setErrorMsg(t('enterCode'));
                }
            })
            .catch((err) => {
                if (!err.response) {
                    console.log(`%cError: ${err}`, 'color: #E8178C');
                    setErrorMsg(t('errorUnexpected'));
                    return;
                }
                console.log(err.response);
                if (err.response.data.detail === 'Jwt expired') {
                    setErrorMsg(t('codeExpired'));
                    setCodeErrorClass('help-block help-block-red text-align-center');
                    setSubmitStep('sms_needed');
                    setSmsCode('');
                    const newHeaders = { ...headers };
                    delete newHeaders.Authorization;
                    setHeaders(newHeaders);
                    return;
                } else if (err.response.data.detail === 'Codes did not match') {
                    setErrorMsg(t('invalidCode'));
                    setCodeErrorClass('help-block help-block-red text-align-center');
                    setSubmitStep('sms_arrived');
                    return;
                }
            });
    };

    /**
     * Checks if form is given all the info it needs and verification code is given, then uses axios
     * to send a POST request to backend. If error, message is shown to user, if no errors,
     * set the next form step.
     *
     * @param {*} e - the onSubmit event
     */
    const submitContacts = (e) => {
        if (!smsCode) {
            setErrorMsg(t('enterCode'));
            setCodeErrorClass('help-block help-block-red text-align-center');
            return;
        }
        const postData = new URLSearchParams();
        postData.append('name', name);
        postData.append('phone', phone);
        postData.append('email', email);
        postData.append('address', address);
        postData.append('disable_sms', blockSms);
        const config = {
            headers: headers,
        };

        axios
            .post(apiUrl, postData, config)
            .then((resp) => {
                setStep((prevStep) => prevStep + 1);
            })
            .catch((err) => {
                if (!err.response) {
                    console.log(`%cError: ${err}`, 'color: #E8178C');
                    setErrorMsg(t('errorUnexpected'));
                    return;
                }
                switch (err.response.status) {
                    case HTTP_NOT_FOUND:
                        // only happens when user tries to block sms but there are no customers found on db
                        setErrorMsg(t('customerNotFound'));
                        console.log(`%cNotFound: ${err.response.data.detail}`, 'color: #E8178C');
                        break;
                    case HTTP_BAD_REQUEST:
                        setErrorMsg(t('checkFields'));
                        console.log(`%cBadRequest: ${err.response.data.detail}`, 'color: #E8178C');
                        break;
                    case HTTP_UNAUTHORIZED:
                        setCommonErrorMsg(t('sessionExp'));
                        setStep((prevStep) => prevStep - 1);
                        console.log(`%cUnauthorized: ${err.response.data.detail}`, 'color: #E8178C');
                        break;
                    default:
                        console.log(`%cError: ${err.response.data.detail}`, 'color: #E8178C');
                        setErrorMsg(t('errorUnexpected'));
                }
            });
    };

    /**
     * Handles if keysms needs to be called for sms verification or form needs to be submitted
     *
     * @param {*} e - the onSubmit event
     */
    const submitContactFormHandler = (e) => {
        if (!name || !phone) {
            setErrorFields(!name && !phone ? 'both' : !name ? 'name' : 'phone');
            setErrorStyle({ borderColor: '#ff2222' });
        } else {
            switch (submitStep) {
                case 'sms_needed':
                    getKeySms(e);
                    break;
                case 'sms_arrived':
                    verifyKeySms(e);
                    break;
                case 'sms_ok':
                    verifyKeySms(e);
                    break;
                default:
                    setSubmitStep('sms_needed');
            }
        }
    };

    const localProps = {
        step: step,
        setStep: setStep,
        infoText: t('phoneInfo'),
        headerText: t('contactsHeader'),
        styleBtn: { background: 'white', borderColor: '#ccc' },
        styleSpan: { border: '3px solid black', color: 'black' },
        submitForm: submitContactFormHandler,
    };

    const localPropsExtra = {
        infoText: t('emailOptional'),
        styleBtn: { background: 'white', borderColor: '#ccc' },
        styleSpan: { border: '3px solid black', color: 'black' },
    };

    return (
        <Fragment>
            <form className="form-signin">
                <Header {...localProps} />
            </form>
            <div className="col-md-offset-2 col-md-8">
                <form id="authenticate-address" className="form-horizontal">
                    <div className="form-group ">
                        <label htmlFor="name" className="col-sm-4 control-label">
                            {t('name')}
                        </label>
                        <div className="col-sm-8">
                            <InputField
                                {...{
                                    inputName: 'name',
                                    placeHolder: t('name'),
                                    inputTitle: '',
                                    inputValue: name,
                                    changeHandler: handleChange,
                                    setHandler: setName,
                                    inputStyle: errorStyle,
                                }}
                            />
                            {errorFields === 'name' || errorFields === 'both' ? (
                                <p className="help-block-red">{t('fieldRequired')}</p>
                            ) : null}
                        </div>
                    </div>

                    <div className="form-group ">
                        <label htmlFor="phone" className="col-sm-4 control-label">
                            {t('phone')}
                        </label>
                        <div className="col-sm-8">
                            <div className="input-group multi-value-parent-container">
                                <InputField
                                    {...{
                                        inputName: 'phone',
                                        placeHolder: t('phone'),
                                        inputTitle: t('phoneInfo'),
                                        inputValue: phone,
                                        changeHandler: handleChange,
                                        setHandler: setPhone,
                                        inputStyle: errorStyle,
                                    }}
                                />
                                <span className="input-group-btn info-button-container">
                                    <QuestionButton {...localProps} />
                                </span>
                            </div>
                            {errorFields === 'phone' || errorFields === 'both' ? (
                                <p className="help-block-red">{errorFieldsMsg}</p>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group ">
                        <label htmlFor="email" className="col-sm-4 control-label">
                            {t('email')}
                        </label>
                        <div className="col-sm-8">
                            <div className="input-group multi-value-parent-container">
                                <InputField
                                    {...{
                                        inputName: 'email',
                                        placeHolder: t('email'),
                                        inputTitle: t('emailOptional'),
                                        inputValue: email,
                                        changeHandler: handleChange,
                                        setHandler: setEmail,
                                    }}
                                />
                                <span className="input-group-btn info-button-container">
                                    <QuestionButton {...localPropsExtra} />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="code" className="col-sm-4 control-label">
                            {t('code')}
                        </label>
                        <div className="col-sm-8">
                            <InputField
                                {...{
                                    inputName: 'code',
                                    placeHolder: t('code'),
                                    inputTitle: '',
                                    inputValue: smsCode,
                                    changeHandler: handleChange,
                                    setHandler: setSmsCode,
                                    inputType: 'password',
                                    inputReadOnly: codeHidden,
                                }}
                            />
                            {errorMsg && <p className={codeErrorClass}>{errorMsg}</p>}
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="no_sms" className="col-sm-4 control-label">
                            {t('noSms')}
                        </label>
                        <div className="col-sm-8">
                            <InputField
                                {...{
                                    inputName: 'no_sms',
                                    inputType: 'checkbox',
                                    classes: 'sms-checkbox',
                                    changeHandler: toggleBlockSms,
                                    inputCheck: blockSms,
                                }}
                            />
                        </div>
                    </div>
                </form>
            </div>
            <br />
            <div className="col-sm-offset-5 col-sm-4">
                <SubmitButton {...localProps} />
                <div className="margin-top-1">{t('infoWillBeUpdated')}</div>
            </div>
        </Fragment>
    );
};

export default ContactForm;
